// CAMBIO: Se añaden las importaciones necesarias de @angular/forms
import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input, Optional, Self, signal, WritableSignal, OnInit, inject } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { FormErrorPipe } from 'src/ui/pipes/form-error.pipe';
import { CustomTooltipDirective } from 'src/ui/directives/custom-tooltip.directive';
import { DropdownRegistryService } from '../dropdown-registry.service';

export interface SelectorOption {
  label: string;
  value: any;
  disabled?: boolean;
  tooltip?: string;
}

@Component({
  selector: 'app-selector',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormErrorPipe, CustomTooltipDirective],
  templateUrl: './selector.component.html',
  styleUrl: './selector.component.scss'
})
export class SelectorComponent implements ControlValueAccessor, OnInit {
  @Input() id?: string;
  @Input() label: string = '';
  @Input() placeholder: string = 'Seleccione una opción...';
  @Input() isReadonly: boolean = false;
  @Input() size: 'small' | 'medium' = 'small';

  @Input()
  set optionsInput(value: (string | SelectorOption)[]) {
    this.options = value.map(opt =>
      typeof opt === 'string' ? { label: opt, value: opt } : opt
    );
    this.writeValue(this.control.value);
  }

  options: SelectorOption[] = [];
  selectedOption: WritableSignal<SelectorOption | null> = signal(null);
  isOpen = signal(false);

  private elRef = inject(ElementRef<HTMLElement>);
  private dropdownRegistry = inject(DropdownRegistryService);

  constructor(
    @Self() @Optional() public ngControl: NgControl,
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  get control(): AbstractControl {
    return this.ngControl?.control ?? new FormControl();
  }

  get isInvalid(): boolean {
    return this.control.invalid && (this.control.dirty || this.control.touched);
  }

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    if (!this.id) {
      this.id = `selector-${Math.random().toString(36).slice(2)}`;
    }

    this.dropdownRegistry.openId$.subscribe((openId) => {
      if (openId === null) {
        this.isOpen.set(false);
        return;
      }
      if (openId !== this.id && this.isOpen()) {
        this.isOpen.set(false);
      }
    });
  }

  toggleDropdown() {
    if (this.isReadonly) return;
    const willOpen = !this.isOpen();
    this.isOpen.set(willOpen);
    this.dropdownRegistry.setOpen(willOpen ? this.id! : null);
    if (!willOpen) {
      this.onTouched();
    }
  }

  selectOption(option: SelectorOption, event: Event) {
    event.stopPropagation();
    if (this.isReadonly) return;
    if (option.disabled) return;

    this.selectedOption.set(option);
    this.isOpen.set(false);
    this.onChange(option.value);
  }

  writeValue(value: any): void {
    const match = this.options.find(opt => opt.value === value);
    this.selectedOption.set(match || null);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isReadonly = isDisabled;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.isOpen()) return;
    const target = event.target as Node;
    if (this.elRef.nativeElement && !this.elRef.nativeElement.contains(target)) {
      this.isOpen.set(false);
      this.dropdownRegistry.closeAll();
      this.onTouched();
    }
  }
}
